// /locations — service-area landing page for the Editorial Warm
// surface. Currently a stub with a single market (Miami-Dade)
// pulled from tmp/handoff/README.md. As the operations team adds real
// city pages or ZIP-level coverage, this page can split into per-market
// detail routes.

import SEOHead from '../components/SEOHead'
import { NalaIcon } from '../components/nala/NalaIcon'
import { nala } from '../components/nala/palette'

const MARKETS = [
  {
    name: 'Miami-Dade',
    blurb:
      "From Coral Gables and Brickell to Pinecrest, Coconut Grove, and the Beaches — same-week home visits across all of Miami-Dade.",
    neighborhoods: [
      'Brickell',
      'Coral Gables',
      'Coconut Grove',
      'South Miami',
      'Pinecrest',
      'Key Biscayne',
      'Miami Beach',
      'Aventura',
      'Doral',
      'Kendall',
    ],
  },
]

const openBooking = () =>
  window.dispatchEvent(new CustomEvent('nala-open-booking'))
const openSms = () =>
  window.dispatchEvent(new CustomEvent('nala-open-sms'))

export default function LocationsPage() {
  return (
    <>
      <SEOHead
        title="Locations — Miami-Dade home vet visits"
        description="Nala provides in-home veterinary care across Miami-Dade County, Florida. See the cities and neighborhoods we serve."
        canonical="https://nala.vet/locations"
      />

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pt-14 pb-10">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider"
          style={{ background: nala.soft, color: nala.ink }}
        >
          Service area
        </span>
        <h1
          className="mt-5 text-4xl sm:text-5xl lg:text-[64px]"
          style={{
            fontFamily: nala.display,
            fontWeight: 400,
            letterSpacing: '-0.025em',
            lineHeight: 1.02,
          }}
        >
          We come to you, across{' '}
          <em
            className="italic"
            style={{ color: nala.clay, fontStyle: 'italic' }}
          >
            South Florida.
          </em>
        </h1>
        <p
          className="italic mt-4 text-lg sm:text-xl max-w-2xl"
          style={{
            fontFamily: nala.display,
            color: nala.mute,
            lineHeight: 1.45,
          }}
        >
          Nala&apos;s vans cover Miami-Dade county, with same-week
          appointments in most neighborhoods.
        </p>
      </section>

      {/* Map strip */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-16">
        <div
          className="overflow-hidden"
          style={{
            background: '#ffffff',
            borderRadius: nala.radius,
            border: `1px solid ${nala.line}`,
          }}
        >
          <img
            src="/img/nala/florida-map.png"
            alt="Map of Florida highlighting Nala's Miami-Dade county service area"
            className="block w-full h-[280px] sm:h-[360px] object-cover"
            style={{ objectPosition: '60% center' }}
          />
        </div>
      </section>

      {/* Market cards */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20">
        <div className="grid gap-5 max-w-2xl mx-auto">
          {MARKETS.map((m) => (
            <article
              key={m.name}
              className="p-7"
              style={{
                background: nala.surface,
                borderRadius: nala.radius,
                border: `1px solid ${nala.line}`,
              }}
            >
              <div className="flex items-start gap-3 mb-3">
                <span
                  className="flex items-center justify-center mt-0.5"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: nala.soft,
                    color: nala.accent,
                  }}
                >
                  <NalaIcon.Pin size={18} color={nala.accent} />
                </span>
                <h2
                  className="text-2xl sm:text-3xl font-medium"
                  style={{ fontFamily: nala.display }}
                >
                  {m.name}
                </h2>
              </div>
              <p
                className="text-[15px] leading-relaxed mb-5"
                style={{ color: nala.mute }}
              >
                {m.blurb}
              </p>
              <div className="flex flex-wrap gap-2">
                {m.neighborhoods.map((n) => (
                  <span
                    key={n}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full"
                    style={{
                      background: nala.bg,
                      color: nala.ink,
                      border: `1px solid ${nala.line}`,
                    }}
                  >
                    {n}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={openBooking}
                  className="inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                  style={{
                    background: nala.accent,
                    color: nala.accentInk,
                    padding: '10px 18px',
                  }}
                >
                  <NalaIcon.Calendar size={14} color={nala.accentInk} />
                  Book in {m.name}
                </button>
                <button
                  type="button"
                  onClick={openSms}
                  className="inline-flex items-center gap-2 rounded-full font-medium text-sm"
                  style={{
                    border: `1px solid ${nala.line}`,
                    color: nala.ink,
                    padding: '10px 14px',
                  }}
                >
                  <NalaIcon.Phone size={13} color={nala.ink} />
                  Check my address
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Out-of-area note */}
      <section className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14 pb-20">
        <div
          className="p-7 sm:p-10 text-center"
          style={{
            background: nala.soft,
            borderRadius: nala.radius,
          }}
        >
          <h3
            className="text-2xl sm:text-3xl"
            style={{
              fontFamily: nala.display,
              fontWeight: 400,
              letterSpacing: '-0.01em',
            }}
          >
            Outside South Florida?
          </h3>
          <p
            className="mt-2 text-[15px] max-w-lg mx-auto"
            style={{ color: nala.mute }}
          >
            We&apos;re expanding fast. Text us your address and we&apos;ll let
            you know when Nala arrives in your neighborhood.
          </p>
          <button
            type="button"
            onClick={openSms}
            className="mt-5 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
            style={{
              background: nala.accent,
              color: nala.accentInk,
              padding: '12px 20px',
            }}
          >
            <NalaIcon.Phone size={14} color={nala.accentInk} />
            Text us your zip
          </button>
        </div>
      </section>
    </>
  )
}
